import { Injectable } from '@angular/core';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { LazyLoadedWidgets } from 'src/app/app-routing-lazy';
import { IWorkItemSettingsResponse } from 'src/app/models/application';
import { ISmartActionMetadata, SmartActionContextItem } from 'src/app/models/smart-actions.models';
import { IComponentConfig, IDisposition, IReasonCodes, IWorkContext, IWorkItem, IWorkItemComposition, IWorkItemDto, IWorkItemEvent, IWorkItemNote } from 'src/app/models/work-items';
import { MemberService } from "src/app/services/member/member.service";
import { NavigationStateProviderService } from 'src/app/shared/navigation-handler/navigation-state-provider.service.ts';
import { AlertService, MessageSeverity } from '../alert.service';
import { Utilities } from '../utilities';
import { WorkItemEndpointService } from './work-item-endpoint.service';
import { ConfigsService } from '../configs/configs.service';

export type OutgoingMode = 'MEMBER' | 'STATE';

export enum WorkMode {
  assigned = 0,
  member = 1
}

@Injectable({
  providedIn: 'root'
})
export class WorkItemService {

  dispositionCodesList: any;

  getWorkItemTypeName(Predicate: any): string {

    let matchItem: any = null;
    let regexp = new RegExp('Type == "(.*?)"');

    let match = regexp.exec(Predicate);
    if (!match || match.length == 0) {
      console.log("No Match Found", Predicate)
    }
    else if (match.length > 0) {
      matchItem = match[1];
    }
    return matchItem;
  }

  workItemSubject = new BehaviorSubject<IWorkItem>(null);
  workItemEvents$ = this.workItemSubject.asObservable();

  public workMode: WorkMode = WorkMode.assigned;
  public workContext = new Subject<IWorkContext>();
  private workItems: IWorkContext;

  public workItemDto = new Subject<IWorkItemDto>();

  constructor(private workItemEndpointService: WorkItemEndpointService,
    private readonly alertService: AlertService,
    private readonly router: Router,
    private readonly activated: ActivatedRoute,
    private readonly navigationStateProviderService: NavigationStateProviderService,
    private readonly configsSevice: ConfigsService) {

    this.workContext.subscribe(workitem => {
      this.workItems = null;
      this.workItems = workitem;
    })
  }

  setWorkItemSubject(subject: IWorkItem) {
    this.workItemSubject.next(subject);
  }

  setWorkMode(mode: WorkMode) {
    this.workMode = mode;
  }

  getAllowAddressEdit(workType: string): boolean {

    let itm = this.workItems?.typeItems.find(item => item.type == workType);

    if (!itm?.configuration?.allowAddressEdit) return false;

    return itm.configuration?.allowAddressEdit;
  }

  getDisplayName(workType: string) {

    let itm = this.workItems?.typeItems.find(item => item.type == workType);

    if (!itm?.configuration?.displayName) return workType;

    return itm.configuration?.displayName;
  }

  getAllowSimultaneousWorkitems(workType: string) {

    let itm = this.workItems?.typeItems.find(item => item.type == workType);

    if (!itm?.configuration?.simultaneousReviewsPermission) return null;

    return itm.configuration?.simultaneousReviewsPermission;
  }

  getWorkItemRoute(workType: string): string {

    let itm = this.workItems?.typeItems.find(item => item.type == workType);

    if (!itm?.configuration?.route) return "workitemselect";

    return itm.configuration?.route;
  }

  getWorkItemOpenTab(workType: string): boolean {

    let itm = this.workItems?.typeItems.find(item => item.type == workType);

    if (!itm?.configuration?.route) return true;

    return itm.configuration?.openBrowserTab;
  }

  getDefaultIcon() {
    return "fa-light fa-user-helmet-safety";
  }

  async getWorkItemDispositionList() {

    let codes = [];

    if (this.dispositionCodesList?.length > 0) {
      return this.dispositionCodesList;
    }

    let configReview = await this.configsSevice.getKeys("WORKITEM_CONFIG");

    if (configReview.length > 0) {
        configReview.forEach((config) => {
            const expression = JSON.parse(config.Expression);
            codes = [...codes, ...(expression?.DispositionCodes ?? [])];

            codes = codes.filter((item, index, self) =>
              index === self.findIndex((t) => t.Code === item.Code) && item.IsComplete !== true
            );
        });
    };

    this.dispositionCodesList = (codes.length > 0) ? [...codes.map(c => c.Code), "OPEN"] : ["OPEN"];
    return this.dispositionCodesList;
  }


  getDefaultDisposition(): IDisposition[] {

    let disp: IDisposition[] = [];
    disp.push({ "code": "COMPLETE", "displayName": "Complete" });
    return disp;
  }

  getDefaultReasonCode(): IReasonCodes[] {

    let disp: IReasonCodes[] = [];
    disp.push({ "code": "Error", "displayName": "Error" });
    return disp;
  }

  getPackageNameForType(workType: string) {

    let itm = this.workItems?.typeItems.find(item => item.type == workType);
    if (!itm) return null;

    return itm.configuration?.packageName;
  }

  getPackageVersionForType(workType: string) {

    let itm = this.workItems?.typeItems.find(item => item.type == workType);
    if (!itm) return null;

    return itm.configuration?.packageVersion;
  }

  getIconForType(workType: string) {

    let itm = this.workItems?.typeItems.find(item => item.type == workType);
    if (!itm) return this.getDefaultIcon();

    return itm.configuration?.icon;
  }

  getPriorityEnabledForType(workType: string) {

    let itm = this.workItems?.typeItems.find(item => item.type == workType);
    if (!itm) return true;

    return itm.configuration?.priorityEnabled ?? true;
  }

  getDueDateEnabledForType(workType: string) {

    let itm = this.workItems?.typeItems.find(item => item.type == workType);
    if (!itm) return true;

    return itm.configuration?.dueDateEnabled ?? true;
  }

  getLockProcessViewForType(workType: string, keyValue: string, defaultValue: string = "default") {

    let itm = this.workItems?.typeItems.find(item => item.type == workType);
    if (!itm) return null;

    if (itm.configuration?.lockProcessViewName) {
      return itm.configuration?.lockProcessViewName[keyValue.toLowerCase()] ?? itm.configuration?.lockProcessViewName[defaultValue];
    }

    return null;
  }

  getLockPermissionForType(workType: string, mode: string = "takeLock") {

    let itm = this.workItems?.typeItems.find(item => item.type == workType);
    if (!itm) return null;

    return itm.configuration?.lockPermission[mode] ?? null;
  }

  getLockedMessageForType(workType: string) {

    let itm = this.workItems?.typeItems.find(item => item.type == workType);
    if (!itm) return "The workitem is locked by another user.";

    return itm.configuration?.lockedMessage ?? "The workitem is locked by another user.";
  }

  getUnLockedMessageForType(workType: string) {

    let itm = this.workItems?.typeItems.find(item => item.type == workType);
    if (!itm) return "The workitem is not locked.";

    return itm.configuration?.unLockedMessage ?? "The workitem is not locked.";
  }

  getStatusEnabledForType(workType: string) {

    let itm = this.workItems?.typeItems.find(item => item.type == workType);
    if (!itm) return true;

    return itm.configuration?.statusEnabled ?? true;
  }

  getGenericScreenEnabledForType(workType: string) {

    let itm = this.workItems?.typeItems.find(item => item.type == workType);
    if (!itm) return true;

    return itm.configuration?.showGenericTab ?? true;
  }

  getNotesScreenEnabledForType(workType: string) {

    let itm = this.workItems?.typeItems.find(item => item.type == workType);
    if (!itm) return true;

    return itm.configuration?.showNotesTab ?? true;
  }

  getCustomScreenEnabledForType(workType: string) {

    let itm = this.workItems?.typeItems.find(item => item.type == workType);
    if (!itm) return true;

    return itm.configuration?.showCustomTab ?? false;
  }

  getNotesEnabledForType(workType: string) {

    let itm = this.workItems?.typeItems.find(item => item.type == workType);
    if (!itm) return false;

    return itm.configuration?.notesEnabled ?? false;
  }

  getQualityReviewItemType(workType: string): boolean {

    let itm = this.workItems?.typeItems.find(item => item.type == workType);
    if (!itm) return false;

    return true;
  }

  getQualityReviewItemName(workType: string): string {

    let itm = this.workItems?.typeItems.find(item => item.configuration.workType == workType);
    if (!itm) return null

    return itm.type;
  }

  getDueDateMinForType(workType: string) {

    let itm = this.workItems?.typeItems.find(item => item.type == workType);
    if (!itm) return 1;

    return itm.configuration?.dueDateMinRange ?? 1;
  }

  getDueDateMaxForType(workType: string) {

    let itm = this.workItems?.typeItems.find(item => item.type == workType);
    if (!itm) return this.getDueDateMinForType(workType) + 10;

    return itm.configuration?.dueDateMaxRange ?? this.getDueDateMinForType(workType) + 10;
  }

  getDispositionsForType(workType: string): IDisposition[] {

    let itm = this.workItems?.typeItems.find(item => item.type == workType);

    if (!itm?.configuration?.dispositionCodes) return this.getDefaultDisposition();

    return itm.configuration?.dispositionCodes;
  }

  getReasonCodesForType(workType: string): IReasonCodes[] {

    let itm = this.workItems?.typeItems.find(item => item.type == workType);

    if (!itm?.configuration?.reasonCodes) return this.getDefaultReasonCode();

    return itm.configuration?.reasonCodes;
  }

  getDispositionsDisplayName(workType: string, name: string): string {

    let itm = this.workItems?.typeItems.find(item => item.type == workType);

    if (!itm?.configuration?.dispositionCodes) return name;

    if (itm.configuration?.dispositionCodes.findIndex(s => s.code == name) == -1) return name;

    return itm.configuration?.dispositionCodes.find(s => s.code == name).displayName;
  }

  getDispositionStatus(workType: string) {

    let itm = this.workItems?.typeItems.find(item => item.type == workType);
    return (itm?.configuration?.dispositionStatus) ? itm?.configuration?.dispositionStatus : null;
  }

  getComponentConfig(workType: string): IComponentConfig {

    let itm = this.workItems?.typeItems.find(item => item.type == workType);

    if (!itm?.configuration?.componentConfig) return null;

    return itm.configuration?.componentConfig;
  }

  getPriorityColor(priority: string): string {

    switch (priority) {
      case "URGENT": {
        return "red";
      }

      case "HIGH": {
        return "orange";
      }

      case "MEDIUM": {
        return "blue";
      }

      case "LOW": {
        return "green";
      }
    }
  }

  // getWorkItemComponentConfig(workitem: IWorkItem) {
  //   return componentConfig.find(componentConfig => componentConfig.type == workitem.type);
  // }

  async getWorkAssignedItems(uniqueId: string, status?: string[], type?: string[]) {

    const context = await this.getWorkItemsByUniqueId(uniqueId, status ?? null, type ?? null);
    this.workContext.next(context);
    this.setWorkMode(WorkMode.assigned);
  }

  async getWorkAssociatedItems(uniqueId: string, status?: string[], type?: string[]) {

    const context = await this.getAssociatedWorkItemsByUniqueId(uniqueId, status ?? null, type ?? null);
    this.workContext.next(context);
    this.setWorkMode(WorkMode.assigned);
  }

  async getWorkMemberItems(tenantId: string, memberId: string, status?: string[], type?: string[]) {

    const context = await this.getWorkItemsByMember(tenantId, memberId, status ?? null, type ?? null);
    this.workContext.next(context);
    this.setWorkMode(WorkMode.member);
  }

  async setWorkItemContext(context: IWorkContext) {
    this.workContext.next(context);
    this.setWorkMode(WorkMode.member);
  }

  async getWorkMemberItemsList(tenantId: string, memberId: string, status?: string[], type?: string[]) {
    return await this.getWorkItemsByMember(tenantId, memberId, status ?? null, type ?? null);
  }

  async getMemberWorkItems(tenantId: string, memberId: string, status?: string[], type?: string[]): Promise<IWorkContext> {
    return await this.getWorkItemsByMember(tenantId, memberId, status ?? null, type ?? null);
  }

  private getWorkItemsByUserName(userName: string, status?: string[], type?: string[]): Promise<IWorkContext> {
    return this.workItemEndpointService.getWorkItemsByUserName(userName, status?.toString(), type?.toString());
  }

  private getWorkItemsByUniqueId(uniqueId: string, status?: string[], type?: string[]): Promise<IWorkContext> {
    return this.workItemEndpointService.getWorkItemsByUniqueId(uniqueId, status?.toString(), type?.toString());
  }

  private getAssociatedWorkItemsByUniqueId(uniqueId: string, status?: string[], type?: string[]): Promise<IWorkContext> {
    return this.workItemEndpointService.getAssociatedWorkItemsByUniqueId(uniqueId, status?.toString(), type?.toString());
  }

  private getWorkItemsByMember(tenantId: string, memberId: string, status?: string[], type?: string[]): Promise<IWorkContext> {
    return this.workItemEndpointService.getWorkItemsByMember(tenantId, memberId, status?.toString(), type?.toString());
  }

  public async getWorkItemById(tenantId: string, id: number): Promise<IWorkContext> {

    const context = await this.workItemEndpointService.getWorkItemsById(tenantId, id);
    this.workContext.next(context);
    return context;
  }

  getWorkItemContext(tenantId: string, workitem: IWorkItem): Observable<IWorkItem> {
    return this.workItemEndpointService.getWorkItemContext(tenantId, workitem);
  }

  updateWorkItems(tenant: string, status: string, workitem: IWorkItemDto): Observable<{ result: boolean }> {
    return this.workItemEndpointService.updateWorkItems(tenant, status, workitem);
  }

  assignWorkItem(tenant: string, id: number, userObjectId: string): Promise<IWorkItemDto> {
    return this.workItemEndpointService.assignWorkItem(tenant, id, userObjectId);
  }

  async createWorkItem(tenant: string, workitem: IWorkItemDto): Promise<IWorkItemDto> {

    let work = await this.workItemEndpointService.createWorkItem(tenant, workitem);
    this.workItemDto.next(work);

    return work;
  }

  addWorkItemNote(tenant: string, workItemId: number, note: IWorkItemNote): Observable<{ result: number }> {
    return this.workItemEndpointService.addWorkItemNote(tenant, workItemId, note);
  }

  getWorkItemsEvents(tenantId: string, Id: number): Observable<IWorkItemEvent[]> {
    return this.workItemEndpointService.getWorkItemsEvents(tenantId, Id);
  }

  saveWorkItemSettings(tenantId: string, Id: number, request: any): Promise<IWorkItemSettingsResponse> {
    return this.workItemEndpointService.saveWorkItemSettings(tenantId, Id, request);
  }

  checkHaveLock(workItem: IWorkItem): string {
    return workItem?.details?.isLocked ? workItem?.details?.lockOwner.toUpperCase() : null;
  }

  updateSelectedWorkItem(workItem: IWorkItem) {
    workItem.priority = workItem.details?.priority;
    workItem.firstName = workItem.details?.firstName;
    workItem.lastName = workItem.details?.lastName;
    workItem.inZone = workItem.details?.inZone;
    workItem.summary = workItem.details?.summary ?? null;
    workItem.summaryTooltip = workItem.details?.summaryTooltip ?? null;
    workItem.base64 = MemberService.getBase64(
      workItem.tenantId,
      workItem.regardingMemberId
    );
  }

  openMemberSmartAction(actionItem: IWorkItem): SmartActionContextItem {

    return new SmartActionContextItem({
      id: `member-opencomponent`,
      displayName: 'Open Member',
      description: 'Open Member',
      disabled: false,
      target: this,
      actionMetadata: {
        item: { item: { FirstName: 'firstName', LastName: 'lastName', IdBase64: actionItem.base64 }, repoMetadata: { tenantId: actionItem.tenantId } }
      } as any,
      command: async (context: ISmartActionMetadata) => {
        const sourceAction = context.sourceAction;
        const handler = context.lazyComponentHandler;
        const target = context.sourceAction.target;
        const config = sourceAction.actionMetadata;

        try {
          this.workItemSubject.next(actionItem);
          await handler.setCurrentCustomView(LazyLoadedWidgets.COMPONENT_MEMBEROPEN, config);
        } catch (error) {
          target.alertService.showStickyMessage('Error', 'Error opening the component', MessageSeverity.error, error);
        }
      }
    } as SmartActionContextItem);
  }

  openWorkItemAction(actionItem: IWorkItem, showOpenWorkItem: boolean = true) {

    return new SmartActionContextItem({

      id: `workitem-open`,
      displayName: 'Open WorkItem',
      description: 'Open WorkItem',
      disabled: false,
      target: this,

      command: async (context: ISmartActionMetadata) => {

        const customComp: IWorkItemComposition = {
          actionItem: actionItem,
          showOpenWorkItem: showOpenWorkItem,
          lazyComponentHandler: context.lazyComponentHandler
        };

        try {
          let route = this.getWorkItemRoute(actionItem.type);
          let openBrowserTab = this.getWorkItemOpenTab(actionItem.type);

          this.openWorkItem(actionItem, route, openBrowserTab, false, true)
        } catch (error) {
          this.alertService.showStickyMessage('Error', 'Error Opening WorkItem', MessageSeverity.error, error);
        }
      }

    } as SmartActionContextItem);
  }

  openWorkItem(actionItem: IWorkItem, route: string, openBrowserTab: boolean, deferred: boolean, mode: boolean = false) {

    const queryParams = new Map<string, string>(
      [
        ["wi-mode", mode ? "true" : "false"],
        ["wi-id", actionItem.id.toString()],
        ["wi-tenantId", actionItem.tenantId],
        ["wi-userview", deferred == true ? "true" : "false"],
      ]);

    const targetRoute = Utilities.routeLastChild(this.activated);
    const reportUrl = this.router.createUrlTree([`/member/${actionItem.base64}/${route}`], { relativeTo: targetRoute, queryParams: Object.fromEntries(queryParams) });

    (!openBrowserTab)
      ? this.router.navigate([`/member/${actionItem.base64}/${route}`], { relativeTo: targetRoute, queryParams: Object.fromEntries(queryParams) })
      : window.open(reportUrl.toString(), '_blank');
  }

  async queryParams(params: ParamMap) {

    let selectedWorkitem = null;

    const mode = params.get("wi-mode");
    const id = params.get("wi-id");
    const tenant = params.get("wi-tenantId");

    if (mode == "true") {

      const items = await this.getWorkItemById(tenant, +id);

      items.typeItems.forEach(item => {
        selectedWorkitem = item.workItems.find(x => x.id.toString() == id);
      });

      if (selectedWorkitem) {
        this.updateSelectedWorkItem(selectedWorkitem);
        this.setWorkItemSubject(selectedWorkitem);
      }

      const queryParams = new Map<string, string>(
        [
          ["wi-mode", mode],
          ["wi-id", id],
          ["wi-tenantId", tenant],
        ]);

      this.navigationStateProviderService.registerParams(queryParams);
    }
  }
}
